/**
 * DecomposeView Component - Enhanced
 *
 * Professional service decomposition dashboard with:
 * - Multiple visualization tabs (Clusters, Coupling Matrix, Service Candidates, Metrics)
 * - D3 circle packing, heatmaps, and force-directed graphs
 * - Real data integration from code analysis
 * - Computed clustering based on paragraph relationships
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react'
import * as d3 from 'd3'
import {
  ArrowLeft, Scissors, Layers, FileCode, GitMerge,
  ChevronDown, X, AlertTriangle, CheckCircle, Info, RefreshCw, Play, Loader2,
  Network, BarChart3, Grid3X3, Target, Zap, Box
} from 'lucide-react'
import { useApplications, useAnalysis } from '../../hooks'
import { applyLayout, LAYOUT_OPTIONS } from '../../utils/graphLayouts'
import {
  getCrossReference,
  getProcedureModel,
  getSummary,
  getJsonArtifact,
  getJobBaseName,
  getMonolithIdentifierResults,
  getMonolithIdentifierArtifact
} from '../../services/codeAnalysisService'
import '@xyflow/react/dist/style.css'
import './DecomposeView.css'

/**
 * Tab configuration
 */
const TABS = {
  'clusters': {
    label: 'Clusters',
    icon: Layers,
    description: 'Logical groupings of paragraphs',
  },
  'coupling': {
    label: 'Coupling Matrix',
    icon: Grid3X3,
    description: 'Inter-cluster dependencies',
  },
  'candidates': {
    label: 'Service Candidates',
    icon: Target,
    description: 'Recommended service boundaries',
  },
  'metrics': {
    label: 'Metrics',
    icon: BarChart3,
    description: 'Decomposition analysis metrics',
  },
}

/**
 * Professional color palette
 */
const COLORS = {
  primary: '#7aa2f7',
  secondary: '#bb9af7',
  success: '#9ece6a',
  warning: '#e0af68',
  danger: '#f7768e',
  info: '#7dcfff',
  clusters: ['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca'],
}

/**
 * Compute clusters from procedure model
 */
function computeClusters(procModel, crossRef) {
  if (!procModel?.paragraphs) return []

  const paragraphs = procModel.paragraphs
  const prefixGroups = {}

  // Group by naming prefix (000-, 100-, 200-, etc.)
  paragraphs.forEach(p => {
    const prefix = p.name.split('-')[0] || 'OTHER'
    if (!prefixGroups[prefix]) {
      prefixGroups[prefix] = {
        id: prefix,
        name: getClusterName(prefix),
        paragraphs: [],
        totalLines: 0,
        performCalls: 0,
        internalCalls: 0,
        externalCalls: 0,
      }
    }
    const lineCount = (p.end_line || 0) - (p.start_line || 0)
    const performCount = p.statements?.filter(s => s.classification === 'PERFORM').length || 0

    prefixGroups[prefix].paragraphs.push({
      name: p.name,
      javaName: p.java_name,
      lines: lineCount,
      performs: performCount,
    })
    prefixGroups[prefix].totalLines += lineCount
    prefixGroups[prefix].performCalls += performCount
  })

  // Calculate coupling between clusters
  const clusters = Object.values(prefixGroups).filter(c => c.paragraphs.length > 0)

  // Compute internal vs external calls
  paragraphs.forEach(p => {
    const prefix = p.name.split('-')[0] || 'OTHER'
    const cluster = prefixGroups[prefix]
    if (!cluster) return

    const performs = p.statements?.filter(s => s.semantic?.type === 'PERFORM') || []
    performs.forEach(perf => {
      const targetPrefix = (perf.semantic?.target || '').split('-')[0]
      if (targetPrefix === prefix) {
        cluster.internalCalls++
      } else {
        cluster.externalCalls++
      }
    })
  })

  // Calculate cohesion and coupling scores
  clusters.forEach(cluster => {
    const total = cluster.internalCalls + cluster.externalCalls
    cluster.cohesion = total > 0 ? cluster.internalCalls / total : 1
    cluster.coupling = total > 0 ? cluster.externalCalls / total : 0
    cluster.isCandidate = cluster.cohesion > 0.6 && cluster.paragraphs.length >= 2
  })

  return clusters.sort((a, b) => b.totalLines - a.totalLines)
}

/**
 * Get human-readable cluster name
 */
function getClusterName(prefix) {
  const names = {
    '000': 'Main Entry',
    '100': 'Initialization',
    '200': 'Core Processing',
    '300': 'Data Validation',
    '400': 'Calculations',
    '500': 'Output Processing',
    '600': 'File Operations',
    '700': 'Error Handling',
    '800': 'Finalization',
    '900': 'Utilities',
  }
  return names[prefix] || `${prefix} Group`
}

/**
 * Clusters Tab - Circle Packing visualization
 */
function ClustersTab({ clusters, loading, error }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    if (!clusters?.length || !svgRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 800
    const height = container.clientHeight || 600

    // Build hierarchy
    const hierarchyData = {
      name: 'PROGRAM',
      children: clusters.map(c => ({
        name: c.name,
        id: c.id,
        cohesion: c.cohesion,
        isCandidate: c.isCandidate,
        children: c.paragraphs.map(p => ({
          name: p.name,
          value: Math.max(p.lines, 10),
          javaName: p.javaName,
        }))
      }))
    }

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    const color = d3.scaleOrdinal().range(COLORS.clusters)

    const pack = d3.pack()
      .size([width - 40, height - 40])
      .padding(4)

    const root = d3.hierarchy(hierarchyData)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value)

    pack(root)

    const node = svg.append('g')
      .attr('transform', 'translate(20, 20)')
      .selectAll('g')
      .data(root.descendants())
      .enter()
      .append('g')
      .attr('transform', d => `translate(${d.x},${d.y})`)

    node.append('circle')
      .attr('r', d => d.r)
      .attr('fill', d => {
        if (d.depth === 0) return 'transparent'
        if (d.depth === 1) return color(d.data.id)
        return d3.color(color(d.parent.data.id)).brighter(0.5)
      })
      .attr('stroke', d => d.depth === 1 ? (d.data.isCandidate ? COLORS.success : '#3b4261') : 'none')
      .attr('stroke-width', d => d.data.isCandidate ? 3 : 1)
      .attr('fill-opacity', d => d.depth === 1 ? 0.3 : 0.7)
      .style('cursor', 'pointer')

    // Labels for clusters
    node.filter(d => d.depth === 1)
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => -d.r + 15)
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#c0caf5')
      .text(d => d.data.name)

    // Labels for larger paragraphs
    node.filter(d => d.depth === 2 && d.r > 20)
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .attr('font-size', '9px')
      .attr('fill', '#1a1b26')
      .text(d => d.data.name.length > 12 ? d.data.name.substring(0, 10) + '..' : d.data.name)

    // Tooltips
    node.append('title').text(d => {
      if (d.depth === 0) return 'Program Structure'
      if (d.depth === 1) return `${d.data.name}\nParagraphs: ${d.children?.length || 0}\nCohesion: ${(d.data.cohesion * 100).toFixed(0)}%\n${d.data.isCandidate ? '✓ Service Candidate' : ''}`
      return `${d.data.name}\nLines: ${d.data.value}\nJava: ${d.data.javaName || 'N/A'}`
    })

  }, [clusters])

  if (loading) return <LoadingState />

  if (!clusters?.length) {
    return (
      <div className="tab-content clusters-tab">
        <div className="tab-header">
          <h3><Layers size={20} /> Paragraph Clusters</h3>
        </div>
        <div className="empty-state">
          <Layers size={48} />
          <h4>No Cluster Data</h4>
          <p>{error || 'Run Code Analysis to generate paragraph clusters.'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-content clusters-tab">
      <div className="tab-header">
        <h3><Layers size={20} /> Paragraph Clusters</h3>
        <p className="tab-description">
          Circle packing showing paragraph groups. Green border = high cohesion (service candidate).
          Size = lines of code.
        </p>
      </div>
      <div className="chart-wrapper" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>
    </div>
  )
}

/**
 * Coupling Matrix Tab - Heatmap
 */
function CouplingTab({ clusters, procModel, loading, error }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    if (!clusters?.length || !procModel?.paragraphs || !svgRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 600
    const margin = { top: 80, right: 20, bottom: 20, left: 100 }

    // Build coupling matrix
    const clusterIds = clusters.map(c => c.id)
    const matrix = []

    clusterIds.forEach((srcId, i) => {
      clusterIds.forEach((tgtId, j) => {
        let count = 0
        procModel.paragraphs.forEach(p => {
          const pPrefix = p.name.split('-')[0]
          if (pPrefix !== srcId) return

          const performs = p.statements?.filter(s => s.semantic?.type === 'PERFORM') || []
          performs.forEach(perf => {
            const targetPrefix = (perf.semantic?.target || '').split('-')[0]
            if (targetPrefix === tgtId) count++
          })
        })
        matrix.push({ source: i, target: j, value: count, srcId, tgtId })
      })
    })

    const cellSize = Math.min(50, (width - margin.left - margin.right) / clusterIds.length)
    const height = margin.top + cellSize * clusterIds.length + margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const maxVal = d3.max(matrix, d => d.value) || 1
    const color = d3.scaleSequential(d3.interpolateBlues).domain([0, maxVal])

    // Cells
    g.selectAll('rect')
      .data(matrix)
      .enter()
      .append('rect')
      .attr('x', d => d.target * cellSize)
      .attr('y', d => d.source * cellSize)
      .attr('width', cellSize - 2)
      .attr('height', cellSize - 2)
      .attr('fill', d => d.value > 0 ? color(d.value) : '#24283b')
      .attr('stroke', '#1a1b26')
      .attr('rx', 3)
      .append('title')
      .text(d => `${d.srcId} → ${d.tgtId}: ${d.value} calls`)

    // Values in cells
    g.selectAll('text.cell-value')
      .data(matrix.filter(d => d.value > 0))
      .enter()
      .append('text')
      .attr('class', 'cell-value')
      .attr('x', d => d.target * cellSize + cellSize / 2)
      .attr('y', d => d.source * cellSize + cellSize / 2)
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .attr('font-size', '10px')
      .attr('fill', d => d.value > maxVal / 2 ? 'white' : '#c0caf5')
      .text(d => d.value)

    // Row labels
    g.selectAll('text.row')
      .data(clusters)
      .enter()
      .append('text')
      .attr('class', 'row')
      .attr('x', -10)
      .attr('y', (d, i) => i * cellSize + cellSize / 2)
      .attr('text-anchor', 'end')
      .attr('dy', 4)
      .attr('font-size', '11px')
      .attr('fill', '#a9b1d6')
      .text(d => d.id)

    // Column labels
    g.selectAll('text.col')
      .data(clusters)
      .enter()
      .append('text')
      .attr('class', 'col')
      .attr('x', (d, i) => i * cellSize + cellSize / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#a9b1d6')
      .text(d => d.id)

    // Title
    svg.append('text')
      .attr('x', margin.left + (cellSize * clusterIds.length) / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', '600')
      .attr('fill', '#c0caf5')
      .text('PERFORM Calls Between Clusters')

    svg.append('text')
      .attr('x', margin.left + (cellSize * clusterIds.length) / 2)
      .attr('y', 45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#787c99')
      .text('(Rows call Columns)')

  }, [clusters, procModel])

  if (loading) return <LoadingState />

  if (!clusters?.length) {
    return (
      <div className="tab-content coupling-tab">
        <div className="tab-header">
          <h3><Grid3X3 size={20} /> Coupling Matrix</h3>
        </div>
        <div className="empty-state">
          <Grid3X3 size={48} />
          <h4>No Coupling Data</h4>
          <p>{error || 'Run Code Analysis to generate coupling metrics.'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-content coupling-tab">
      <div className="tab-header">
        <h3><Grid3X3 size={20} /> Coupling Matrix</h3>
        <p className="tab-description">
          Heatmap showing PERFORM calls between paragraph groups. Darker = more coupling.
          Diagonal (same cluster) = internal cohesion.
        </p>
      </div>
      <div className="chart-wrapper" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>
    </div>
  )
}

/**
 * Service Candidates Tab
 */
function CandidatesTab({ clusters, loading, error }) {
  if (loading) return <LoadingState />

  if (!clusters?.length) {
    return (
      <div className="tab-content candidates-tab">
        <div className="tab-header">
          <h3><Target size={20} /> Service Candidates</h3>
        </div>
        <div className="empty-state">
          <Target size={48} />
          <h4>No Service Candidates</h4>
          <p>{error || 'Run Code Analysis to identify service candidates.'}</p>
        </div>
      </div>
    )
  }

  const candidates = clusters.filter(c => c.isCandidate)
  const nonCandidates = clusters.filter(c => !c.isCandidate)

  return (
    <div className="tab-content candidates-tab">
      <div className="tab-header">
        <h3><Target size={20} /> Service Candidates</h3>
        <p className="tab-description">
          Clusters with high cohesion ({'>'}60%) are recommended as service boundaries.
        </p>
      </div>

      <div className="candidates-content">
        {/* Candidates */}
        <div className="candidates-section">
          <h4><CheckCircle size={16} /> Recommended Services ({candidates.length})</h4>
          <div className="candidates-grid">
            {candidates.map((c, i) => (
              <div key={c.id} className="candidate-card recommended">
                <div className="candidate-header">
                  <span className="cluster-badge" style={{ backgroundColor: COLORS.clusters[i % COLORS.clusters.length] }}>
                    {c.id}
                  </span>
                  <span className="candidate-name">{c.name}</span>
                </div>
                <div className="candidate-stats">
                  <div className="stat">
                    <span className="stat-value">{c.paragraphs.length}</span>
                    <span className="stat-label">Paragraphs</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{c.totalLines}</span>
                    <span className="stat-label">Lines</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{(c.cohesion * 100).toFixed(0)}%</span>
                    <span className="stat-label">Cohesion</span>
                  </div>
                </div>
                <div className="candidate-recommendation">
                  <CheckCircle size={14} />
                  <span>High cohesion - low coupling</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Non-candidates */}
        <div className="candidates-section">
          <h4><AlertTriangle size={16} /> Needs Refactoring ({nonCandidates.length})</h4>
          <div className="candidates-grid">
            {nonCandidates.map((c, i) => (
              <div key={c.id} className="candidate-card needs-work">
                <div className="candidate-header">
                  <span className="cluster-badge muted">{c.id}</span>
                  <span className="candidate-name">{c.name}</span>
                </div>
                <div className="candidate-stats">
                  <div className="stat">
                    <span className="stat-value">{c.paragraphs.length}</span>
                    <span className="stat-label">Paragraphs</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{(c.cohesion * 100).toFixed(0)}%</span>
                    <span className="stat-label">Cohesion</span>
                  </div>
                </div>
                <div className="candidate-warning">
                  <AlertTriangle size={14} />
                  <span>
                    {c.paragraphs.length < 2 ? 'Too small for service' : 'High coupling with other clusters'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Metrics Tab
 */
function MetricsTab({ clusters, procModel, summary, loading, error }) {
  if (loading) return <LoadingState />

  if (!clusters?.length && !procModel) {
    return (
      <div className="tab-content metrics-tab">
        <div className="tab-header">
          <h3><BarChart3 size={20} /> Decomposition Metrics</h3>
        </div>
        <div className="empty-state">
          <BarChart3 size={48} />
          <h4>No Metrics Data</h4>
          <p>{error || 'Run Code Analysis to generate metrics.'}</p>
        </div>
      </div>
    )
  }

  const totalParagraphs = procModel?.paragraphs?.length || 0
  const totalLines = summary?.source_line_count || 0
  const candidates = clusters.filter(c => c.isCandidate)
  const avgCohesion = clusters.length > 0
    ? clusters.reduce((sum, c) => sum + c.cohesion, 0) / clusters.length
    : 0
  const avgCoupling = clusters.length > 0
    ? clusters.reduce((sum, c) => sum + c.coupling, 0) / clusters.length
    : 0

  return (
    <div className="tab-content metrics-tab">
      <div className="tab-header">
        <h3><BarChart3 size={20} /> Decomposition Metrics</h3>
        <p className="tab-description">
          Analysis of service decomposition readiness
        </p>
      </div>

      <div className="metrics-content">
        <div className="metrics-grid">
          <div className="metric-card highlight">
            <Layers size={24} />
            <span className="metric-value">{clusters.length}</span>
            <span className="metric-label">Clusters Identified</span>
          </div>
          <div className="metric-card">
            <Target size={24} />
            <span className="metric-value">{candidates.length}</span>
            <span className="metric-label">Service Candidates</span>
          </div>
          <div className="metric-card">
            <Box size={24} />
            <span className="metric-value">{totalParagraphs}</span>
            <span className="metric-label">Total Paragraphs</span>
          </div>
          <div className="metric-card">
            <Zap size={24} />
            <span className="metric-value">{totalLines.toLocaleString()}</span>
            <span className="metric-label">Total Lines</span>
          </div>
        </div>

        <div className="health-section">
          <h4>Decomposition Health</h4>
          <div className="health-grid">
            <div className="health-item">
              <div className="health-label">Average Cohesion</div>
              <div className="health-bar-container">
                <div
                  className={`health-bar ${avgCohesion > 0.6 ? 'good' : avgCohesion > 0.4 ? 'medium' : 'poor'}`}
                  style={{ width: `${avgCohesion * 100}%` }}
                ></div>
              </div>
              <div className="health-value">{(avgCohesion * 100).toFixed(0)}%</div>
            </div>
            <div className="health-item">
              <div className="health-label">Average Coupling</div>
              <div className="health-bar-container">
                <div
                  className={`health-bar ${avgCoupling < 0.3 ? 'good' : avgCoupling < 0.5 ? 'medium' : 'poor'}`}
                  style={{ width: `${avgCoupling * 100}%` }}
                ></div>
              </div>
              <div className="health-value">{(avgCoupling * 100).toFixed(0)}%</div>
            </div>
            <div className="health-item">
              <div className="health-label">Service Readiness</div>
              <div className="health-bar-container">
                <div
                  className={`health-bar ${candidates.length >= clusters.length * 0.5 ? 'good' : 'medium'}`}
                  style={{ width: `${(candidates.length / Math.max(clusters.length, 1)) * 100}%` }}
                ></div>
              </div>
              <div className="health-value">{candidates.length}/{clusters.length} clusters</div>
            </div>
          </div>
        </div>

        <div className="cluster-table-section">
          <h4>Cluster Details</h4>
          <div className="data-grid">
            <table>
              <thead>
                <tr>
                  <th>Cluster</th>
                  <th>Name</th>
                  <th>Paragraphs</th>
                  <th>Lines</th>
                  <th>Cohesion</th>
                  <th>Coupling</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {clusters.map(c => (
                  <tr key={c.id} className={c.isCandidate ? 'candidate' : ''}>
                    <td className="cluster-id">{c.id}</td>
                    <td>{c.name}</td>
                    <td>{c.paragraphs.length}</td>
                    <td>{c.totalLines}</td>
                    <td>
                      <span className={`score ${c.cohesion > 0.6 ? 'good' : 'low'}`}>
                        {(c.cohesion * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td>
                      <span className={`score ${c.coupling < 0.3 ? 'good' : 'high'}`}>
                        {(c.coupling * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td>
                      {c.isCandidate ? (
                        <span className="status-badge good"><CheckCircle size={12} /> Ready</span>
                      ) : (
                        <span className="status-badge warning"><AlertTriangle size={12} /> Needs Work</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Summary Panel
 */
function SummaryPanel({ clusters, loading, error }) {
  if (loading) {
    return (
      <div className="summary-panel">
        <div className="panel-section">
          <h4>Summary</h4>
          <div className="loading-placeholder">Loading...</div>
        </div>
      </div>
    )
  }

  if (!clusters?.length) {
    return (
      <div className="summary-panel">
        <div className="panel-section">
          <h4>Summary</h4>
          <div className="loading-placeholder">{error || 'No clusters found. Run Code Analysis first.'}</div>
        </div>
      </div>
    )
  }

  const candidates = clusters.filter(c => c.isCandidate)
  const totalLines = clusters.reduce((sum, c) => sum + c.totalLines, 0)
  const avgCohesion = clusters.reduce((sum, c) => sum + c.cohesion, 0) / clusters.length

  return (
    <div className="summary-panel">
      <div className="panel-section">
        <h4><Scissors size={16} /> Decomposition Summary</h4>
        <div className="stats-list">
          <div className="stat-row">
            <span className="stat-label">Clusters</span>
            <span className="stat-value">{clusters.length}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Service Candidates</span>
            <span className="stat-value success">{candidates.length}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Total Lines</span>
            <span className="stat-value">{totalLines.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Avg Cohesion</span>
            <span className="stat-value">{(avgCohesion * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="panel-section">
        <h4><Target size={16} /> Top Candidates</h4>
        <div className="top-items">
          {candidates.slice(0, 5).map(c => (
            <div key={c.id} className="top-item candidate">
              <span className="item-name">{c.id} - {c.name}</span>
              <span className="item-value">{(c.cohesion * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Loading state
 */
function LoadingState() {
  return (
    <div className="loading-state">
      <Loader2 size={32} className="spin" />
      <span>Analyzing decomposition...</span>
    </div>
  )
}

/**
 * Main DecomposeView component
 */
function DecomposeViewInner({ onBack, analysisContext = null }) {
  const [activeTab, setActiveTab] = useState('clusters')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Data state - code analysis
  const [crossReference, setCrossReference] = useState(null)
  const [procedureModel, setProcedureModel] = useState(null)
  const [summary, setSummary] = useState(null)

  // Data state - monolith identifier
  const [decompositionStrategy, setDecompositionStrategy] = useState(null)
  const [businessCapabilities, setBusinessCapabilities] = useState(null)
  const [modularityMetrics, setModularityMetrics] = useState(null)
  const [detectedPatterns, setDetectedPatterns] = useState(null)

  const jobContext = analysisContext
  const { currentApplication } = useApplications()
  const { analyzing, progress, runMonolithIdentification } = useAnalysis(currentApplication?.id)

  // Fetch all data
  const fetchData = useCallback(async () => {
    console.log('[DecomposeView] fetchData called, jobContext:', jobContext)

    if (!jobContext) {
      console.log('[DecomposeView] No jobContext, skipping fetch')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Try to load monolith identifier data first (primary source for decomposition)
      const monolithJobId = jobContext?.jobs?.monolithidentifier_job_id || jobContext?.monolithIdentifierJobId
      console.log('[DecomposeView] monolithJobId:', monolithJobId)

      if (monolithJobId) {
        try {
          const miResults = await getMonolithIdentifierResults(monolithJobId)
          console.log('[DecomposeView] miResults:', miResults)

          // Fetch the actual artifact files
          if (miResults?.json_artifacts?.includes('decomposition_strategy.json')) {
            console.log('[DecomposeView] Fetching monolith identifier artifacts...')
            const [decomp, caps, metrics, patterns] = await Promise.allSettled([
              getMonolithIdentifierArtifact(monolithJobId, 'decomposition_strategy'),
              getMonolithIdentifierArtifact(monolithJobId, 'business_capabilities'),
              getMonolithIdentifierArtifact(monolithJobId, 'modularity_metrics'),
              getMonolithIdentifierArtifact(monolithJobId, 'detected_patterns'),
            ])

            console.log('[DecomposeView] decomposition_strategy:', decomp.status)
            console.log('[DecomposeView] business_capabilities:', caps.status)
            console.log('[DecomposeView] modularity_metrics:', metrics.status)
            console.log('[DecomposeView] detected_patterns:', patterns.status)

            if (decomp.status === 'fulfilled') setDecompositionStrategy(decomp.value)
            if (caps.status === 'fulfilled') setBusinessCapabilities(caps.value)
            if (metrics.status === 'fulfilled') setModularityMetrics(metrics.value)
            if (patterns.status === 'fulfilled') setDetectedPatterns(patterns.value)
          }
        } catch (err) {
          console.warn('[DecomposeView] Could not load monolith identifier data:', err.message)
        }
      } else {
        console.log('[DecomposeView] No monolith identifier job available')
      }

      // Also try to load code analysis data for additional context
      const codeAnalysisJobId = jobContext?.jobs?.codeanalysis_job_id
      console.log('[DecomposeView] codeAnalysisJobId:', codeAnalysisJobId)

      if (codeAnalysisJobId && !codeAnalysisJobId.startsWith('mi_')) {
        // Discover baseName from job artifacts if not provided
        let baseName = jobContext?.baseName
        console.log('[DecomposeView] initial baseName:', baseName)

        if (!baseName || baseName === jobContext?.mainProgram?.toLowerCase()) {
          const discoveredBaseName = await getJobBaseName(codeAnalysisJobId)
          console.log('[DecomposeView] discovered baseName:', discoveredBaseName)
          if (discoveredBaseName) {
            baseName = discoveredBaseName
          }
        }

        console.log('[DecomposeView] final baseName:', baseName)

        if (baseName) {
          const [crossRef, procModel, summaryData] = await Promise.allSettled([
            getCrossReference(codeAnalysisJobId),
            getProcedureModel(codeAnalysisJobId, baseName),
            getSummary(codeAnalysisJobId, baseName),
          ])

          if (crossRef.status === 'fulfilled') setCrossReference(crossRef.value)
          if (procModel.status === 'fulfilled') setProcedureModel(procModel.value)
          if (summaryData.status === 'fulfilled') setSummary(summaryData.value)
        }
      } else {
        console.log('[DecomposeView] No code analysis job available - using monolith identifier data only')
      }
    } catch (err) {
      console.error('[DecomposeView] Failed to fetch data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [jobContext])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Compute clusters from real data - prefer monolith identifier data
  const clusters = useMemo(() => {
    // If we have decomposition strategy from monolith identifier, use that
    if (decompositionStrategy?.recommended_services?.length > 0) {
      console.log('[DecomposeView] Using decomposition strategy data for clusters')
      return decompositionStrategy.recommended_services.map((service, index) => ({
        id: `cluster-${index}`,
        name: service.service_name,
        capability: service.business_capability,
        programs: service.programs || [],
        paragraphs: service.programs || [], // For compatibility
        totalLoc: service.total_loc || 0,
        complexity: service.extraction_complexity || 'medium',
        effort: service.estimated_effort_weeks || 0,
        priority: service.priority || 'medium',
        godObjects: service.extracted_from_god_objects || [],
      }))
    }

    // Fallback to computing from procedure model
    return computeClusters(procedureModel, crossReference)
  }, [decompositionStrategy, procedureModel, crossReference])

  const handleRunDecompose = useCallback(async () => {
    try {
      await runMonolithIdentification({ sourceType: 'cobol' })
    } catch (err) {
      console.error('Decomposition failed:', err)
    }
  }, [runMonolithIdentification])

  return (
    <div className="decompose-view enhanced">
      {/* Header */}
      <div className="decompose-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            <span>Back to Workflow</span>
          </button>
          <div className="view-title">
            <h2>Decompose: {jobContext?.mainProgram || 'Service Analysis'}</h2>
            <span className="view-subtitle">Microservice Decomposition Analysis</span>
          </div>
        </div>

        <div className="header-center">
          <div className="tab-switcher">
            {Object.entries(TABS).map(([tab, config]) => {
              const Icon = config.icon
              return (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                  title={config.description}
                >
                  <Icon size={16} />
                  <span className="tab-label">{config.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="header-right">
          <button className="refresh-btn" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="decompose-content">
        {!jobContext && !loading && (
          <div className="no-data-state">
            <Scissors size={48} />
            <h3>No Decomposition Data Available</h3>
            <p>Run the Decompose node in your workflow first.</p>
            <button className="back-to-workflow-btn" onClick={onBack}>
              <ArrowLeft size={18} /> Back to Workflow
            </button>
          </div>
        )}

        {jobContext && (
          <div className="dashboard-layout">
            <div className="chart-container">
              {activeTab === 'clusters' && (
                <ClustersTab clusters={clusters} loading={loading} error={error} />
              )}
              {activeTab === 'coupling' && (
                <CouplingTab clusters={clusters} procModel={procedureModel} loading={loading} error={error} />
              )}
              {activeTab === 'candidates' && (
                <CandidatesTab clusters={clusters} loading={loading} error={error} />
              )}
              {activeTab === 'metrics' && (
                <MetricsTab clusters={clusters} procModel={procedureModel} summary={summary} loading={loading} error={error} />
              )}
            </div>

            <div className="side-panel">
              <SummaryPanel clusters={clusters} loading={loading} error={error} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Wrapper with ReactFlowProvider
function DecomposeView(props) {
  return (
    <ReactFlowProvider>
      <DecomposeViewInner {...props} />
    </ReactFlowProvider>
  )
}

export default DecomposeView
